import React, { useEffect, useState } from "react";
import { Box, styled } from "@mui/material";
import SpacesContent from "./SpacesContent";
import { TransformedSection } from "@services/facilitytype";
import FacilityInput from "./FacilityInput";
import { Regulation } from "@services/regulations";
import RegulationDetail from "@components/Regulations/RegulationDetail";
import FacilityValidationScreen from "./FacilityValidationScreen";
import NonHealthcareFacilityScreen from "./NonHealthcareFacilityScreen";
import { useLocation } from "react-router-dom";
import LandingPage from "./LandingPage";
import MyFacilityDetails from "./MyFacilityDetails";
import NetworkPage from "./NetworkPage";
import NetworkIntelligencePage from "./network/NetworkIntelligencePage";
import FacilityNetworkPage from "./FacilityNetworkPage";
import MyNetworkGatePage from "./facilityNetwork/MyNetworkGatePage";
import { resolveNetworkDestinationFromPath } from "@utils/dashboardNav";
import ReviewParentLinkRequestPage from "./ReviewParentLinkRequestPage";
import ReviewUnlinkRequestPage from "./ReviewUnlinkRequestPage";
import AddChildFacilitiesPage from "./AddChildFacilitiesPage";
import NetworkFacilityDetailPage from "./network/NetworkFacilityDetailPage";
import type { NetworkSubTabId } from "./NetworkTab";
import Pricing from "@screens/Pricing/Pricing";
import MultiFacilityOrderSummary from "./MultiFacilityOrderSummary";
import VerificationInProgressPage from "./VerificationInProgressPage";
import VerificationFailedPage from "./VerificationFailedPage";
import FacilityRestrictedScreen from "./FacilityRestrictedScreen";
import { canViewByName, FEATURE_NAMES } from "@utils/permissions";
import { preferMyFacilityViewForPostPurchase } from "@utils/postPurchaseFacilitySelection";
import { userCanUseNetworkFacility } from "@utils/userUtils";
import { DASHBOARD } from "./dashboard/dashboardTheme";

const SpacesContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "networkShell" && prop !== "fillHeight" && prop !== "whiteShell",
})<{
  networkShell?: boolean;
  fillHeight?: boolean;
  whiteShell?: boolean;
}>(({ networkShell, fillHeight, whiteShell }) => ({
  backgroundColor: whiteShell
    ? "#ffffff"
    : networkShell
      ? DASHBOARD.shellBg
      : "#ecfcff",
  paddingTop: fillHeight ? 0 : "5px",
  borderRadius: "12px",
  width: "100%",
  minHeight: fillHeight ? 0 : "100%",
  height: fillHeight ? "100%" : "auto",
  flex: fillHeight ? "1 1 auto" : "1 0 auto",
  display: "flex",
  flexDirection: "column",
  paddingBottom: fillHeight ? 0 : "5px",
  boxSizing: "border-box",
  alignItems: "stretch",
  overflow: fillHeight ? "hidden" : "visible",
}));

type SpacesView =
  | "spaces"
  | "facilityInput"
  | "MyFacility"
  | "parentFacility"
  | "network"
  | "network-intelligence"
  | "facility-network"
  | "facility-network-gate"
  | "facility-network-review-request"
  | "facility-network-review-unlink-request"
  | "facility-network-add-children"
  | "facility-network-declare-parent"
  | "network-facility"
  | "facilityValidation"
  | "regulationDetail"
  | "changepassword"
  | "pricing"
  | "orderSummary"
  | "verificationPending"
  | "verificationFailed"
  | "facilityRestricted";

interface SpacesProps {
  onNavigate?: (path: string) => void;
  currentView?: SpacesView;
}

export default function Spaces({ onNavigate }: SpacesProps) {
  const location = useLocation();
  const routeState = location.state as {
    currentView?: SpacesView;
    ccn?: string;
    networkId?: number;
    facilityId?: number;
    networkSubTab?: NetworkSubTabId;
    networkName?: string;
    /** Vertical the user picked in VerticalPreviewSwitcher when they had no
     *  matching facility. Used to route non-healthcare verticals to their
     *  dedicated setup flow while healthcare stays on the NPI flow. */
    preselectedVertical?: string;
  };
  const preselectedVertical = routeState?.preselectedVertical;
  const rawPathPart = location.pathname.split("/").pop(); // "facilities", "home", "MyFacility", etc.
  const pathPart =
    rawPathPart === "facilities" || rawPathPart === "Facilities"
      ? "spaces"
      : rawPathPart; // normalize /dashboard/facilities → Regulations Library view
  const canViewSetupFacility = canViewByName(FEATURE_NAMES.SETUP_FACILITY);
  const canUseNetworkFacility = userCanUseNetworkFacility();
  const pathNetworkDestination = resolveNetworkDestinationFromPath(
    location.pathname,
  );

  const resolveSpacesView = (view: SpacesView): SpacesView => {
    if (view === "facility-network-declare-parent") {
      return "facility-network-add-children";
    }
    if (view === "spaces" && !canViewSetupFacility) {
      return "MyFacility";
    }
    if (!canUseNetworkFacility) {
      if (view === "facility-network" || view === "facility-network-gate") {
        return "facility-network-gate";
      }
      if (
        view === "network" ||
        view === "network-facility" ||
        view === "network-intelligence" ||
        view === "parentFacility"
      ) {
        return "network-intelligence";
      }
    }
    return view;
  };

  const [currentView, setCurrentView] = useState<SpacesView>(() => {
    // Pending post-purchase: never open Regulations Library on /facilities.
    if (preferMyFacilityViewForPostPurchase(routeState?.currentView)) {
      return resolveSpacesView("MyFacility");
    }
    // Check route state first, then pathPart, then default
    if (routeState?.currentView) {
      return resolveSpacesView(routeState.currentView);
    }
    if (pathNetworkDestination) {
      return resolveSpacesView(pathNetworkDestination.view);
    }
    // Default to "MyFacility" if user doesn't have Setup Facility permission
    const defaultView = (pathPart as SpacesView) ?? "spaces";
    return resolveSpacesView(defaultView);
  });

  useEffect(() => {
    if (preferMyFacilityViewForPostPurchase(routeState?.currentView)) {
      const view = resolveSpacesView("MyFacility");
      setCurrentView(view);
      setActiveNavItem(view);
      return;
    }

    // Prioritize routeState.currentView over pathPart
    if (routeState?.currentView) {
      const view = resolveSpacesView(routeState.currentView);
      setCurrentView(view);
      setActiveNavItem(view);
      return;
    }

    if (pathNetworkDestination) {
      const view = resolveSpacesView(pathNetworkDestination.view);
      setCurrentView(view);
      setActiveNavItem(view);
      return;
    }

    // Only set from pathPart if no routeState.currentView is provided
    if (
      pathPart === "spaces" ||
      pathPart === "MyFacility" ||
      pathPart === "parentFacility" ||
      pathPart === "network" ||
      pathPart === "intel" ||
      pathPart === "network-intelligence" ||
      pathPart === "facility-network" ||
      pathPart === "facility-network-gate" ||
      pathPart === "facility-network-add-children" ||
      pathPart === "facility-network-declare-parent" ||
      pathPart === "network-facility" ||
      pathPart === "facilityValidation" ||
      pathPart === "changepassword" ||
      pathPart === "pricing" ||
      pathPart === "orderSummary" ||
      pathPart === "verificationPending" ||
      pathPart === "facilityRestricted"
    ) {
      const mappedPathPart =
        pathPart === "intel" ? "network-intelligence" : pathPart;
      const view = resolveSpacesView(
        (mappedPathPart === "spaces" && !canViewSetupFacility
          ? "MyFacility"
          : mappedPathPart) as SpacesView,
      );
      setCurrentView(view);
    }
  }, [pathPart, pathNetworkDestination, routeState, canViewSetupFacility, canUseNetworkFacility]);

  const [, setActiveNavItem] = useState<string>(currentView);
  const [selectedCategory, setSelectedCategory] = useState(
    "hospitals--inpatient"
  );

  React.useEffect(() => {
    if (preferMyFacilityViewForPostPurchase(routeState?.currentView)) {
      setCurrentView("MyFacility");
      setActiveNavItem("MyFacility");
      return;
    }
    if (routeState?.currentView) {
      const view = resolveSpacesView(
        (routeState.currentView === "spaces" && !canViewSetupFacility
          ? "MyFacility"
          : routeState.currentView) as SpacesView,
      );
      setCurrentView(view);
      setActiveNavItem(view);
      return;
    }
    if (pathNetworkDestination) {
      const view = resolveSpacesView(pathNetworkDestination.view);
      setCurrentView(view);
      setActiveNavItem(view);
      return;
    }
    if (pathPart === "changepassword") {
      setCurrentView("changepassword");
    }
  }, [routeState, pathPart, pathNetworkDestination, canViewSetupFacility, canUseNetworkFacility]);

  const [selectedSection, setSelectedSection] =
    useState<TransformedSection | null>(null);
  const [selectedRegulation, setSelectedRegulation] =
    useState<Regulation | null>(null);


  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handleSectionClick = (section: TransformedSection) => {
    setSelectedSection(section);
    setCurrentView("facilityInput");
  };

  const handleBackToContent = () => {
    setCurrentView("spaces");
    setSelectedSection(null);
  };

  const handleBackFromRegulation = () => {
    setCurrentView("spaces");
    setSelectedRegulation(null);
  };

  const handleFacilityValidation = (data: {
    state: string;
    city: string;
    facilityName: string;
    country: string;
  }) => {
    // After facility selection, go to multi-facility order summary (skip Select Plan)
    setCurrentView("orderSummary");
  };

  const handleRegulationClick = (regulation: Regulation) => {
    setSelectedRegulation(regulation);
    setCurrentView("regulationDetail");
  };
  if (currentView === "spaces") {
    return (
      <SpacesContent
        selectedCategory={selectedCategory}
        onCategorySelect={handleCategorySelect}
        onSectionClick={handleSectionClick}
        onRegulationClick={handleRegulationClick}
      />
    );
  }

  const useNetworkShell =
    currentView === "network-facility" ||
    (currentView === "network" && routeState?.networkId != null);
  const fillHeight =
    currentView === "parentFacility" ||
    currentView === "facility-network" ||
    currentView === "facility-network-gate" ||
    currentView === "facility-network-review-request" ||
    currentView === "facility-network-review-unlink-request" ||
    currentView === "facility-network-add-children" ||
    currentView === "network-intelligence" ||
    currentView === "network-facility" ||
    currentView === "network";

  return (
    <SpacesContainer
      networkShell={useNetworkShell}
      fillHeight={fillHeight}
      whiteShell={currentView === "orderSummary"}
    >
      {currentView === "MyFacility" ? (
        <MyFacilityDetails
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
          onSectionClick={handleSectionClick}
        />
      ) : currentView === "facility-network-gate" ? (
        <MyNetworkGatePage />
      ) : currentView === "network-intelligence" ? (
        <NetworkIntelligencePage />
      ) : currentView === "parentFacility" ||
        currentView === "facility-network" ||
        (currentView === "network" && routeState?.networkId == null) ? (
        <FacilityNetworkPage />
      ) : currentView === "network" ? (
        <NetworkPage />
      ) : currentView === "facility-network-review-request" ? (
        <ReviewParentLinkRequestPage />
      ) : currentView === "facility-network-review-unlink-request" ? (
        <ReviewUnlinkRequestPage />
      ) : currentView === "facility-network-add-children" ? (
        <AddChildFacilitiesPage />
      ) : currentView === "network-facility" && routeState?.ccn ? (
        <NetworkFacilityDetailPage
          ccn={routeState.ccn}
          networkId={routeState.networkId}
          facilityId={routeState.facilityId}
          networkName={routeState.networkName}
          networkSubTab={routeState.networkSubTab}
        />
      ) : currentView === "facilityInput" ? (
        <FacilityInput
          selectedSection={selectedSection}
          selectedCategory={selectedCategory}
          onBack={handleBackToContent}
        />
      ) : currentView === "facilityValidation" ? (
        // Non-healthcare verticals (food_service, aviation_*, contracts, ...)
        // get a dedicated screen that searches the local Facility table by
        // vertical instead of the NPI registry, and skips healthcare-only
        // validation steps. Healthcare and the no-vertical legacy path keep
        // FacilityValidationScreen unchanged.
        preselectedVertical && preselectedVertical !== "healthcare" ? (
          <NonHealthcareFacilityScreen
            preselectedVertical={preselectedVertical}
          />
        ) : (
          <FacilityValidationScreen
            onValidate={handleFacilityValidation}
            preselectedVertical={preselectedVertical}
          />
        )
      ) : currentView === "regulationDetail" && selectedRegulation ? (
        <RegulationDetail
          regulation={selectedRegulation}
          onBack={handleBackFromRegulation}
        />
      ) : currentView === "verificationPending" ? (
        <VerificationInProgressPage
          onOk={() => {
            setCurrentView("facilityValidation");
            setActiveNavItem("facilityValidation");
          }}
        />
      ) : currentView === "verificationFailed" ? (
        <VerificationFailedPage />
      ) : currentView === "facilityRestricted" ? (
        <FacilityRestrictedScreen />
      ) : currentView === "pricing" ? (
        <Pricing />
      ) : currentView === "orderSummary" ? (
        <MultiFacilityOrderSummary />
      ) : (
        <LandingPage />
      )}
    </SpacesContainer>
  );
}
